import { makeAutoObservable } from 'mobx'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { tiers } from '@gap/utils/tier/tier-config'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { apiClient } from 'api/trpc/trpc-client'
import { AccountCancelled, RequestMoreSeats, RequestSent, UpgradeTierInfo } from './OrgUpgrade'
import OrgGuardStore from '../../OrgGuardStore'
import { BillingStore } from 'features/org/edit/billing/BillingStore'
import { Timeouts } from '@gap/utils/date-utils'
import { pluralise } from '@gap/utils/language-utils'
import Loader from 'reusable/mobx-helpers/Loader'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'

interface OrgSubscriptionTierStoreParams {
   orgGuardStore: OrgGuardStore
}

export class OrgSubscriptionTierStore {
   sp
   // Reset the request confirmation on close so the next open starts from the request screen again.
   upgradePlanModal = new ModalState({ onClose: () => this.resetRequest() })
   planPicker: BillingStore
   tiers = tiers
   requestLoader = new Loader(false)
   requestSent = false
   userCount: number = 0
   activeUserCount: number = 0

   constructor(sp: OrgSubscriptionTierStoreParams) {
      this.sp = sp
      this.planPicker = new BillingStore({ orgGuardStore: sp.orgGuardStore, orgSubscriptionTierStore: this })
      makeAutoObservable(this)
   }

   get orgId() {
      return this.sp.orgGuardStore.userOrg.orgId
   }

   get userOrg() {
      return this.sp.orgGuardStore.userOrg
   }

   get orgIsOnTrial() {
      return this.sp.orgGuardStore.isOnTrial
   }

   get orgIsOnExpiredTrial() {
      return this.sp.orgGuardStore.isOnExpiredTrial
   }

   get orgIsCancelled() {
      return this.userOrg.accountStatus === 'cancelled'
   }

   get orgAccessIsRevoked() {
      return this.orgIsOnExpiredTrial || this.orgIsCancelled
   }

   /** A paid or converted-trial Chargebee org, viewed by someone who can maintain it. */
   get canManageSeats() {
      const { orgGuardStore } = this.sp
      return (
         this.userOrg.orgType === 'advisor' &&
         orgGuardStore.doesUserHaveFeaturePermission({ featureId: 'my-organisation', actionId: 'maintain' }) &&
         !!orgGuardStore.chargebee &&
         orgGuardStore.chargebee.subscriptionStatus !== 'cancelled' &&
         !orgGuardStore.isOnTrial &&
         !this.orgIsCancelled
      )
   }

   // Two outcomes only (mirrors the `showBilling` gate: chargebee || isOnTrial):
   // - Orgs that can self-serve a plan change see the Chargebee picker (PlanSelection): orgs already on
   //   Chargebee, plus orgs still on a Trial (who convert by picking a plan).
   // - Other legacy orgs (on a paid legacy tier, not yet migrated to Chargebee) can't self-serve, so they
   //   ask our team to add seats / update their plan. Temporary until every legacy org is migrated.
   get ViewComponent() {
      if (this.requestSent) {
         return <RequestSent />
      }
      if (this.orgIsCancelled) {
         return <AccountCancelled />
      }
      if (this.sp.orgGuardStore.chargebee || this.sp.orgGuardStore.isOnTrial) {
         return <UpgradeTierInfo />
      }
      return <RequestMoreSeats />
   }

   get currentOrgTierName() {
      return this.sp.orgGuardStore.userData.userOrg?.tierName
   }

   // Legacy seat ceiling still reads from tier-config when the org has no explicit tierMaxUsers
   // (see OrgTeamMemberListStore.orgMaxUsers). Kept for that fallback only.
   get tier() {
      return this.tiers.find(({ name }) => name === this.currentOrgTierName)
   }

   get seatsInUseText() {
      const maxUsers = this.userOrg?.tierMaxUsers
      if (typeof maxUsers === 'number') {
         return `${this.userCount} of ${maxUsers} team member ${pluralise(maxUsers, 'seat is', 'seats are')} in use.`
      }
      return `${this.userCount} team member ${pluralise(this.userCount, 'seat is', 'seats are')} in use.`
   }

   /** Opens the plan picker in place of the upgrade modal; `onClosed` runs once when it closes. */
   openChangePlan = (params: { title: string; onClosed?: () => void }) => {
      this.upgradePlanModal.close()
      void this.planPicker.openChangePlan(params)
   }

   openAddSeatsOrChangePlan = () => {
      this.openChangePlan({ title: 'Add seats or change plan' })
   }

   // Rounded up so a fresh 14 day trial reads 14, and the final partial day reads 1 rather than 0
   daysUntilDate = (dateString: string) => {
      return Math.ceil((new Date(dateString).getTime() - Date.now()) / Timeouts.day)
   }

   fetch = async () => {
      const userCount = await apiClient.org.getUserCountInOrg.query({ orgId: this.orgId })
      this.userCount = userCount.count
      this.activeUserCount = userCount.activeCount
   }

   // Email our sales team that this org wants more seats / a plan update, then show the confirmation screen.
   // Reuses the existing notifySalesOfUpgrade procedure (sends the advisor a confirmation and notifies sales).
   requestMoreSeats = async () => {
      await this.requestLoader.executeLoading(async () => {
         try {
            await apiClient.org.notifySalesOfUpgrade.query()
            this.requestSent = true
         } catch {
            snackbarError('We had trouble sending your request. Please try again.')
         }
      })
   }

   resetRequest = () => {
      this.requestSent = false
   }

   finishRequest = () => {
      this.upgradePlanModal.close()
   }

   handleRevokedAccess = () => {
      if (this.orgAccessIsRevoked) {
         // Navigate to /clients, which is the only route we will allow unhindered use of
         this.sp.orgGuardStore.sp.regionRouterStore.navigate(`/org/${this.orgId}/clients`)

         this.upgradePlanModal.open()
         return true
      }

      return false
   }
}

export const { Provider: OrgSubscriptionTierStoreProvider, useProvidedStore: useOrgSubscriptionTierStore } =
   createStoreContext(OrgSubscriptionTierStore)
